import { WorkflowContext } from '@samelogic/steps';
import { Schema } from 'jsonschema';
import { StepConfig } from '../../../../models';
import { buildInputPropsFromContext } from './buildInputPropsFromContext';

import { contextFactory } from '../../../__tests__/factories';

describe('buildInputPropsFromContext()', () => {
  type SuccessCase = {
    name: string;
    config: StepConfig;
    schema: Schema;
    context: WorkflowContext;
    expected: unknown;
  };

  const schema: Schema = {
    additionalProperties: false,
    properties: {
      strProp: {
        type: 'string'
      },
      boolProp: {
        type: 'boolean'
      },
      numProp1: {
        type: 'integer'
      },
      numProp2: {
        type: 'integer'
      },
      jsonObjectProp: {
        type: 'string'
      }
    },
    required: ['boolProp', 'numProp1', 'numProp2', 'strProp']
  };
  describe.each<SuccessCase>([
    {
      name: 'when the config contains no templates',
      config: {
        name: 'test',
        uses: '',
        props: {
          strProp: 'hello',
          boolProp: true,
          numProp1: 1,
          numProp2: 2,
          nested: {
            strProp: 'hello'
          }
        }
      },
      schema: schema,
      context: contextFactory.build(),
      expected: {
        strProp: 'hello',
        boolProp: true,
        numProp1: 1,
        numProp2: 2,
        nested: {
          strProp: 'hello'
        }
      }
    },
    {
      name: 'when the config contains a template',
      config: {
        name: 'test',
        uses: '',
        props: {
          nameProp: '{{ metadata.name}}',
          userIdProp: '{{ identity.userId}}',
          identityProp: '{{ identity.properties.customProp }}',
          strProp:
            '{{ events.step-name.hello-event-name.data.hello }} {{ events.step-name.world-event-name.data.world }}',
          boolProp: '{{ events.step1-name.event1-name.data.boolProp }}',
          numProp1: 1,
          numProp2: '{{ events.step1-name.event1-name.data.numProp2Map }}',
          jsonObjectProp: '{{ events.step1-name.event1-name.data }}',
          nested: {
            userIdProp: '{{ identity.userId}}'
          }
        }
      },
      schema: schema,
      context: contextFactory.build({
        metadata: { name: 'test-name' },
        identity: {
          userId: '12345',
          properties: { customProp: 'custom-prop-value' }
        },
        events: {
          'step-name': {
            'hello-event-name': { data: { hello: 'hello' } },
            'world-event-name': { data: { world: 'world!' } }
          },
          'step1-name': {
            'event1-name': { data: { boolProp: false, numProp2Map: 2 } }
          }
        }
      }),
      expected: {
        nameProp: 'test-name',
        strProp: 'hello world!',
        userIdProp: '12345',
        identityProp: 'custom-prop-value',
        boolProp: false, // this ensures that the template is converted back to a bool
        numProp1: 1,
        numProp2: 2, // this ensures that the template is converted back to a number
        jsonObjectProp: `{"boolProp":false,"numProp2Map":2}`, // validate that object is stringified
        nested: {
          userIdProp: '12345'
        }
      }
    }
  ])('$name', ({ context, config, schema, expected }) => {
    it('should build the input props from the context using the config', () => {
      const got = buildInputPropsFromContext(context, config, schema);
      expect(got).toEqual(expected);
    });
  });
});
